import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = authed

  const bootstrap = useCallback(async () => {
    // Session lives in a HttpOnly cookie; ask the server who we are.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (error) {
      if (error.response && error.response.status !== 401) {
        console.warn("Session check failed unexpectedly.", error);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const handleAuthResponse = (data) => {
    // No token persistence in the browser — the secure cookie is authoritative.
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      return { ok: true, user: handleAuthResponse(data) };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      return { ok: true, user: handleAuthResponse(data) };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const demoLogin = async () => {
    try {
      const { data } = await api.post("/auth/demo");
      return { ok: true, user: handleAuthResponse(data) };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.warn("Server logout call failed; clearing local session anyway.", error);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, demoLogin, logout, refresh: bootstrap }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
